/**
 * A custom element that renders a stripe.js payment method messaging component
 * 
 * Requires the following attributes:
 *  - publishable-key: The Stripe API publishable key
 *  - amount: The payment amount, in pence (e.g. £150 would be 15000)
 *  - currency: The currency code
 *  - country-code: The country code
 *  
 *  The rationale for using a custom element is to enable async loading
 *  of the stripe.js module without blocking. We can asynchronously load
 *  the library when the custom element gets mounted, and attach the result
 *  to the DOM when everything's ready.
 *  
 *  This also means that there's no (or at least very limited) overhead for
 *  any tenants that don't require the element.
 *  
 *  @example <stripe-messaging amount="10000" publishable-key="pk_..." currency="GBP" country-code="GB">
 *  @constructor
 */
export class PaymentMessagingElement extends HTMLElement {
    constructor() {
        super();
    }
    
    async connectedCallback() {
        
        // The "pure" version of the lib doesn't have any side effects, so Stripe itself isn't loaded
        // until we actually call `loadStripe()`
        const {loadStripe} = await import("@stripe/stripe-js/pure");
        
        // Grab attributes
        const publishableKey = this.getAttribute("publishable-key");
        const currency = this.getAttribute("currency");
        const countryCode = this.getAttribute("country-code");
        const amount = this.getAttribute("amount");
        
        // Prepare the appearance settings
        const appearance = {
            theme: "night"
        }
        
        // Load stripe.js
        const stripe = await loadStripe(publishableKey);
        const elements = stripe.elements({appearance});
        
        // Prepare the element itself
        const options = {
            amount: Number(amount),
            currency,
            countryCode,
        }
        
        const messagingElement = elements.create("paymentMethodMessaging", options);
        
        // Mount to the DOM
        messagingElement.mount(this);
    }
}